import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import ImportBagsButton from './ImportBagsButton';
import './BagsTable.css';

function BagsTable({ bags, students, schoolId, onRefreshBags }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBag, setNewBag] = useState({
    serialNumber: '',
    model: '',
    brand: 'GenFaraday'
  });
  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Create a map of student ID to student name for quick lookup
  const studentMap = useMemo(() => {
    const map = {};
    students.forEach(student => {
      map[student.id] = `${student.firstName} ${student.lastName}`;
    });
    return map;
  }, [students]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Archive / restore one unit — the staff-side twin of the portal's archive button. Archiving
  // releases the unit from whichever student holds it, which is why it asks first.
  const toggleArchived = useCallback(async (bag) => {
    const next = !bag.archived;
    if (next && !window.confirm(
      `Archive ${bag.serialNumber}?\n\n` +
      `It drops out of this school's lists and totals, and is released from any student holding it. ` +
      `You can put it back later with "Show Archived".`
    )) return;

    setArchivingId(bag.id);
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/bags/${bag.id}/archived`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify({ archived: next })
      });
      if (!response.ok) {
        alert(`Could not ${next ? 'archive' : 'restore'} that product (${response.status}).`);
        return;
      }
      if (onRefreshBags) await onRefreshBags();
    } catch (error) {
      console.error('Error changing archived state:', error);
      alert('Could not reach the server. Please try again.');
    } finally {
      setArchivingId(null);
    }
  }, [onRefreshBags]);

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: 'Serial #',
        accessor: 'serialNumber',
      },
      {
        Header: 'Student',
        accessor: row => row.studentDbId ? studentMap[row.studentDbId] || row.studentName : '-',
        id: 'studentName',
      },
      {
        Header: 'Brand',
        accessor: 'brand',
      },
      {
        Header: 'Model',
        accessor: 'model',
      },
      {
        Header: 'Issue Date',
        accessor: 'issueDate',
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: 'Damaged',
        accessor: 'damaged',
        sortType: (rowA, rowB) => {
          const aVal = rowA.original.damaged ? 1 : 0;
          const bVal = rowB.original.damaged ? 1 : 0;
          return aVal - bVal;
        },
        Cell: ({ value }) => {
          if (value === true) return <span className="status-damaged">Yes</span>;
          return <span>-</span>;
        },
      },
      {
        Header: 'Lost',
        accessor: 'lost',
        sortType: (rowA, rowB) => {
          const aVal = rowA.original.lost ? 1 : 0;
          const bVal = rowB.original.lost ? 1 : 0;
          return aVal - bVal;
        },
        Cell: ({ value }) => {
          if (value === true) return <span className="status-lost">Yes</span>;
          return <span>-</span>;
        },
      },
      {
        // Archive retires a unit: it drops out of the school's lists, totals and scans, and is
        // released from whichever student held it. Reversible — tick "Show Archived" to find one
        // and put it back. Only shown for archived rows when the archived filter is on, so the
        // column stays quiet in normal use.
        Header: '',
        id: 'archiveAction',
        disableSortBy: true,
        Cell: ({ row }) => {
          const bag = row.original;
          const busy = archivingId === bag.id;
          return (
            <button
              type="button"
              className={bag.archived ? 'bags-restore-btn' : 'bags-archive-btn'}
              onClick={() => toggleArchived(bag)}
              disabled={busy}
            >
              {busy ? 'Working…' : bag.archived ? 'Restore' : 'Archive'}
            </button>
          );
        },
      },
    ],
    [studentMap, archivingId, toggleArchived]
  );

  // Filter and sort bags based on search query and archived status
  const filteredBags = useMemo(() => {
    let filtered = bags;
    
    // Filter out archived bags if checkbox is not checked
    if (!showArchived) {
      filtered = filtered.filter(bag => !bag.archived);
    }
    
    // Apply search query (using debounced value)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(bag => {
        const serialNumber = bag.serialNumber ? bag.serialNumber.toLowerCase() : '';
        const studentName = bag.studentDatabaseId ? (studentMap[bag.studentDatabaseId] || bag.studentName || '').toLowerCase() : '';
        const brand = bag.brand ? bag.brand.toLowerCase() : '';
        const model = bag.model ? bag.model.toLowerCase() : '';
        
        return serialNumber.includes(query) || studentName.includes(query) || brand.includes(query) || model.includes(query);
      });
    }
    
    // Sort by issue date descending, then serial number descending
    filtered = [...filtered].sort((a, b) => {
      // Compare issue dates (most recent first)
      const dateA = a.issueDate ? new Date(a.issueDate).getTime() : 0;
      const dateB = b.issueDate ? new Date(b.issueDate).getTime() : 0;
      
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      
      // If dates are equal, compare serial numbers descending
      const serialA = a.serialNumber || '';
      const serialB = b.serialNumber || '';
      return serialB.localeCompare(serialA);
    });
    
    return filtered;
  }, [bags, debouncedSearchQuery, showArchived, studentMap]);

  // Use the useTable hook with sorting and pagination
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredBags,
      initialState: { 
        pageIndex: 0, 
        pageSize: 50
      },
    },
    useSortBy,
    usePagination
  );

  // Reset to first page when filters change
  useEffect(() => {
    gotoPage(0);
  }, [showArchived, debouncedSearchQuery, gotoPage]);

  // File a tag report for a serial the portal could not add because the record on file disagrees.
  // Lands in the Tag Reports queue next to the ones school staff file from the app.
  const fileTagReport = async (serialNumber, model) => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');
      const response = await fetch(`${apiEndpoint}/admin/tag-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify({
          serialNumber,
          physicalSerial: serialNumber,
          sku: model || '',
          schoolId,
          note: 'Serial discrepancy raised while adding this bag to the school.'
        })
      });
      if (response.ok) {
        alert('Tag report filed. It is in the Tag Reports queue for reconciliation.');
      } else {
        alert(`Could not file the tag report (${response.status}). Please tell support directly.`);
      }
    } catch (error) {
      console.error('Error filing tag report:', error);
      alert('Could not reach the server to file the tag report. Please tell support directly.');
    }
  };

  const handleAddBag = async () => {
    try {
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
      const username = localStorage.getItem('admin_username');
      const password = localStorage.getItem('admin_password');

      const requestBody = {
        serialNumber: newBag.serialNumber,
        model: newBag.model,
        brand: newBag.brand
      };

      const response = await fetch(`${apiEndpoint}/admin/schools/${schoolId}/bags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${username}:${password}`)
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Add bag response:', response);

      if (response.ok) {
        console.log('Bag added successfully');
        alert('Bag added successfully');
        
        // Close modal and reset form
        setShowAddModal(false);
        setNewBag({ serialNumber: '', model: 'MAX-LOCK-V2', brand: 'GenFaraday' });

        // Refresh the bags data
        if (onRefreshBags) {
          await onRefreshBags();
        }
      } else {
        console.error('Failed to add bag:', response.status);

        // The server returns a raw string for old-style 400s and JSON for a serial discrepancy
        // (409). A discrepancy is not a dead end: the record on file may be a stale duplicate, so
        // offer the tag report that gets support to reconcile the two instead of only saying "no".
        let errorMessage = 'Failed to add bag. Please try again.';
        let discrepancy = null;
        try {
          const raw = await response.text();
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              errorMessage = parsed.error || raw;
              if (parsed.canTagReport) discrepancy = parsed;
            } catch (e) {
              errorMessage = raw;  // pre-existing plain-text error
            }
          }
        } catch (e) {
          console.error('Could not read error response:', e);
        }

        if (discrepancy) {
          const filed = window.confirm(
            `${errorMessage}\n\nFile a tag report now?`
          );
          if (filed) await fileTagReport(newBag.serialNumber, newBag.model);
        } else {
          alert(errorMessage);
        }

        // Close modal and reset form
        setShowAddModal(false);
        setNewBag({ serialNumber: '', model: 'MAX-LOCK-V2', brand: 'GenFaraday' });
      }
    } catch (error) {
      console.error('Error adding bag:', error);
      alert('Error adding bag. Please try again.');
      
      // Close modal and reset form
      setShowAddModal(false);
      setNewBag({ serialNumber: '', model: '', brand: 'GenFaraday' });
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewBag({ serialNumber: '', model: 'MAX-LOCK', brand: 'GenFaraday' });
  };

  return (
    <>
      <div className="bags-header">
        <div className="bags-left">
          <h3>Faraday Bags ({filteredBags.length})</h3>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span>Show Archived</span>
          </label>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search by serial #, student, brand, or model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button 
                className="clear-search"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="bags-right">
          <button onClick={() => setShowAddModal(true)} className="add-bag-button">
            + Add Bag
          </button>
          <ImportBagsButton schoolId={schoolId} onImported={onRefreshBags} />
        </div>
      </div>
      
      {bags.length === 0 ? (
        <div className="no-data">No bags found for this school</div>
      ) : filteredBags.length === 0 ? (
        <div className="no-data">No bags match your search</div>
      ) : (
        <div className="table-container">
          <table {...getTableProps()} className="bags-table">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, j) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={j}>
                      <div className="th-content">
                        {column.render('Header')}
                        <span className="sort-indicator">
                          {column.isSorted
                            ? column.isSortedDesc
                              ? ' ↓'
                              : ' ↑'
                            : ''}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, j) => (
                      <td {...cell.getCellProps()} key={j}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="pagination">
            <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
              {'<<'}
            </button>
            <button onClick={() => previousPage()} disabled={!canPreviousPage}>
              {'<'}
            </button>
            <button onClick={() => nextPage()} disabled={!canNextPage}>
              {'>'}
            </button>
            <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
              {'>>'}
            </button>
            <span>
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
            <span>
              | Go to page:{' '}
              <input
                type="number"
                defaultValue={pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0
                  gotoPage(page)
                }}
                style={{ width: '100px' }}
              />
            </span>
            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
              }}
            >
              {[10, 25, 50, 100, 200].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Bag</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="serialNumber">Serial Number</label>
                <input
                  type="text"
                  id="serialNumber"
                  value={newBag.serialNumber}
                  onChange={(e) => setNewBag({ ...newBag, serialNumber: e.target.value })}
                  placeholder="Enter serial number"
                />
              </div>
              <div className="form-group">
                <label htmlFor="model">SKU</label>
                <input
                  type="text"
                  id="model"
                  value={newBag.model}
                  onChange={(e) => setNewBag({ ...newBag, model: e.target.value })}
                  placeholder="Enter SKU (e.g. GFSB-MAX-LOCK)"
                />
              </div>
              <div className="form-group">
                <label htmlFor="brand">Brand</label>
                <input
                  type="text"
                  id="brand"
                  value={newBag.brand}
                  onChange={(e) => setNewBag({ ...newBag, brand: e.target.value })}
                  placeholder="Enter brand"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              <button 
                className="modal-button submit" 
                onClick={handleAddBag}
                disabled={!newBag.serialNumber || !newBag.model || !newBag.brand}
              >
                Add Bag
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default BagsTable;

